import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { CapService } from '../api';

interface ICapWidgetParams {
  merchantId: string | null;
  amount: string | null;
  currency: string | null;
  cryptoCurrency: string | null;
  email: string | null;
  env: string | null;
}

interface IUseCapWidgetResult {
  params: ICapWidgetParams;
  sessionId: string | null;
  checkoutId: string | null;
  isLoading: boolean;
  error: string | null;
}

/**
 * Custom hook for CAP Widget page
 * Handles widget initialization from URL parameters
 */
export const useCapWidget = (): IUseCapWidgetResult => {
  const location = useLocation();
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [checkoutId, setCheckoutId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const initializationAttemptedRef = useRef(false);

  // Parse URL parameters
  const params = useMemo((): ICapWidgetParams => {
    const searchParams = new URLSearchParams(location.search);
    
    return {
      merchantId: searchParams.get('merchantId'),
      amount: searchParams.get('amount'),
      currency: searchParams.get('currency'),
      cryptoCurrency: searchParams.get('cryptoCurrency'),
      email: searchParams.get('email'),
      env: searchParams.get('env'),
    };
  }, [location.search]);

  /**
   * Initialize widget: create session and update with payment data automatically
   * Widget will be rendered directly on the page (no iframe)
   */
  const initializeWidget = useCallback(async () => {
    if (!params.env) {
      setError('env parameter is required in URL. Please add ?env=YOUR_ENV to the URL');
      return;
    }
    if (!params.merchantId || !params.amount || !params.currency) {
      setError('Missing required parameters: merchantId, amount, currency');
      return;
    }

    // Prevent double initialization (React StrictMode calls effects twice)
    if (initializationAttemptedRef.current) {
      console.log('Initialization already attempted, skipping...');
      return;
    }
    
    initializationAttemptedRef.current = true;
    setIsLoading(true);
    setError(null);

    try {
      // Save env to localStorage for status page redirect
      localStorage.setItem('cap_env', params.env);
      console.log('Saved env to localStorage:', params.env);

      const capService = new CapService(params.env);

      // Step 1: Create session (POST)
      console.log('Creating session with merchantId:', params.merchantId);
      const createResponse = await capService.createSession({
        integer_tracking_id: params.merchantId,
        crypto_currency: params.cryptoCurrency || undefined,
      });

      const newSessionId = createResponse.session_id;
      setSessionId(newSessionId);
      console.log('Session created:', newSessionId);

      // Step 2: Update session with payment data (PATCH)
      console.log('Updating session with payment data');
      const updateResponse = await capService.updateSession(newSessionId, {
        checkout_data: {
          amount: parseFloat(params.amount),
          currency: params.currency,
        },
        customer: {
          email: params.email || undefined,
        },
      });

      const newCheckoutId = updateResponse.checkout_id;
      setCheckoutId(newCheckoutId);
      console.log('Session updated, checkoutId:', newCheckoutId);

      // Widget will be initialized directly on the page (not via iframe/URL)

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to initialize widget';
      setError(errorMessage);
      console.error('Widget initialization error:', err);
      // Reset flag on error to allow retry
      initializationAttemptedRef.current = false;
    } finally {
      setIsLoading(false);
    }
  }, [params]);

  // Auto-initialize on mount if all required params are present
  useEffect(() => {
    if (!params.env) {
      setError('env parameter is required in URL. Example: ?merchantId=123&amount=100&currency=USD&env=YOUR_ENV');
    } else if (!params.merchantId || !params.amount || !params.currency) {
      setError('Missing required URL parameters: merchantId, amount, currency');
    } else {
      initializeWidget();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    params,
    sessionId,
    checkoutId,
    isLoading,
    error,
  };
};

